import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutList, BarChart3, LogOut, ArrowUpRight } from "lucide-react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Helmet } from "react-helmet-async";
import { siteConfig } from "@/config/siteConfig";

const linkBase =
  "flex items-center gap-3 px-4 py-2.5 text-sm font-medium tracking-wide rounded-[3px] transition-colors";

const AdminLayout = ({ children, title = "Admin" }) => {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>{`${title} — ${siteConfig.COMPANY_NAME}`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="min-h-screen bg-[var(--bg-alt)] text-[var(--charcoal)] flex">
        {/* Sidebar */}
        <aside
          className="hidden lg:flex flex-col w-64 bg-[var(--navy)] text-white sticky top-0 h-screen"
          data-testid="admin-sidebar"
        >
          <div className="px-5 py-6 border-b border-white/10">
            <button
              onClick={() => navigate("/admin")}
              className="text-left block"
              data-testid="admin-brand"
            >
              <div className="font-heading text-lg font-semibold tracking-tight">
                {siteConfig.COMPANY_NAME}
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/60 mt-1">
                Admin
              </div>
            </button>
          </div>
          <nav className="flex-1 py-4 px-3 space-y-1">
            <NavLink
              to="/admin"
              end
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5 hover:text-white"}`
              }
              data-testid="admin-nav-leads"
            >
              <LayoutList size={16} /> Leads
            </NavLink>
            <NavLink
              to="/admin/insights"
              className={({ isActive }) =>
                `${linkBase} ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5 hover:text-white"}`
              }
              data-testid="admin-nav-insights"
            >
              <BarChart3 size={16} /> Insights
            </NavLink>
          </nav>
          <div className="px-3 pb-4 space-y-1 border-t border-white/10 pt-4">
            <a
              href="/"
              className={`${linkBase} text-white/60 hover:bg-white/5 hover:text-white`}
              data-testid="admin-nav-visit-site"
            >
              <ArrowUpRight size={16} /> Visit site
            </a>
            <button
              onClick={logout}
              className={`${linkBase} text-white/75 hover:bg-white/5 hover:text-white w-full text-left`}
              data-testid="admin-signout-button"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </aside>

        {/* Main column */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Top bar */}
          <header className="bg-white border-b border-[var(--border)] px-5 lg:px-8 py-3 flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-4">
              {/* Mobile brand */}
              <button
                onClick={() => navigate("/admin")}
                className="lg:hidden font-heading text-[15px] font-semibold text-[var(--navy)] tracking-tight"
                data-testid="admin-brand-mobile"
              >
                {siteConfig.COMPANY_NAME}
              </button>
              <div className="hidden lg:block eyebrow">{title}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-[var(--slate)] hidden sm:block" data-testid="admin-user-label">
                Signed in as <span className="text-[var(--charcoal)] font-medium">{user?.username || "admin"}</span>
              </div>
              <button
                onClick={logout}
                className="btn-outline text-xs !py-1.5 !px-3"
                data-testid="admin-topbar-signout"
              >
                Sign out
              </button>
            </div>
          </header>

          {/* Mobile nav */}
          <div className="lg:hidden bg-white border-b border-[var(--border)] px-5 py-2 flex gap-4 text-sm">
            <NavLink to="/admin" end className={({ isActive }) => isActive ? "text-[var(--navy)] font-semibold" : "text-[var(--slate)]"} data-testid="admin-mnav-leads">
              Leads
            </NavLink>
            <NavLink to="/admin/insights" className={({ isActive }) => isActive ? "text-[var(--navy)] font-semibold" : "text-[var(--slate)]"} data-testid="admin-mnav-insights">
              Insights
            </NavLink>
          </div>

          <main className="flex-1 px-5 lg:px-8 py-6 lg:py-8 min-w-0">
            {children}
          </main>
        </div>
      </div>
    </>
  );
};

export default AdminLayout;
